import { useEffect, useMemo, useState } from 'react';
import type { EstadoPuerta, Puerta, Rol } from '@matias-asset-os/contracts';
import { ESTADOS_PUERTA } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { fechaLarga } from '../componentes.js';
import { Icono } from '../componentes/Iconos.js';
import { CargaRapida } from './CargaRapida.js';
import { FichaPuerta } from './Ficha.js';
import { MapaPuertas } from './MapaPuertas.js';

/**
 * PUERTA A PUERTA.
 *
 * Tres vistas de lo mismo y una sola pregunta detrás: ¿qué hemos recorrido ya,
 * y qué queda?
 *
 *   - El MAPA contesta «qué calles hicimos», que es lo que se mira antes de
 *     salir y lo que impide recorrer la misma manzana dos veces.
 *   - Las ZONAS lo dicen en números: cuántas puertas por barrio y en cuántas
 *     abrieron. Una zona con dos puertas registradas no está recorrida, está
 *     empezada, y eso sólo se ve contando.
 *   - La LISTA es para trabajar: filtrar por estado y abrir la que toque.
 *
 * El botón de registrar está siempre a la vista, en todas las vistas: es lo que
 * más se usa, con diferencia.
 */

interface Props {
  idioma: Idioma;
  rol: Rol;
  onVolver: () => void;
}

type Vista = 'mapa' | 'zonas' | 'lista';

export function Puertas({ idioma, rol, onVolver }: Props) {
  const [puertas, setPuertas] = useState<Puerta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>('mapa');
  const [seleccionada, setSeleccionada] = useState<string | null>(null);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [registrando, setRegistrando] = useState(false);
  const [filtro, setFiltro] = useState<EstadoPuerta | 'TODAS'>('TODAS');
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    api
      .puertas()
      .then((r) => setPuertas(r.puertas))
      .catch(() => setError(t(idioma, 'pap.noSeCargaron')));
  }, [idioma]);

  const visibles = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return (puertas ?? []).filter((p) => {
      if (filtro !== 'TODAS' && p.estado !== filtro) return false;
      if (!texto) return true;
      return [p.direccion, p.zona, p.municipio, p.contactoNombre ?? '']
        .join(' ')
        .toLowerCase()
        .includes(texto);
    });
  }, [puertas, filtro, busqueda]);

  /**
   * Las zonas recorridas, contadas.
   *
   * «Ya hicimos Cala Major» es una frase que no significa nada si no se sabe
   * cuántos portales se registraron allí. Aquí están, con cuántos abrieron y
   * cuándo se pasó por última vez.
   */
  const zonas = useMemo(() => {
    const mapa = new Map<string, { puertas: number; atendidas: number; ultima: string }>();
    for (const p of puertas ?? []) {
      const clave = [p.municipio || '—', p.zona || '—'].join(' · ');
      const actual = mapa.get(clave) ?? { puertas: 0, atendidas: 0, ultima: '' };
      actual.puertas += 1;
      if (p.estado !== 'NO_ATENDIO') actual.atendidas += 1;
      if (p.visitadaEn > actual.ultima) actual.ultima = p.visitadaEn;
      mapa.set(clave, actual);
    }
    return [...mapa.entries()].sort((a, b) => b[1].puertas - a[1].puertas);
  }, [puertas]);

  const laAbierta = (puertas ?? []).find((p) => p.id === abierta) ?? null;

  const alRegistrar = (nueva: Puerta) => setPuertas((p) => [nueva, ...(p ?? [])]);
  const alGuardar = (g: Puerta) =>
    setPuertas((p) => (p ?? []).map((x) => (x.id === g.id ? g : x)));

  if (registrando) {
    return (
      <CargaRapida idioma={idioma} onCerrar={() => setRegistrando(false)} onRegistrada={alRegistrar} />
    );
  }

  if (laAbierta) {
    return (
      <main>
        <FichaPuerta
          idioma={idioma}
          puerta={laAbierta}
          esDireccion={rol === 'DIRECTOR'}
          onCerrar={() => setAbierta(null)}
          onGuardada={alGuardar}
          onEliminada={(id) => {
            setPuertas((p) => (p ?? []).filter((x) => x.id !== id));
            setAbierta(null);
          }}
        />
      </main>
    );
  }

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'pap.titulo')}</h1>
          <p>{t(idioma, 'pap.sub')}</p>
        </div>
        <div className="acciones">
          <button type="button" className="pap-registrar" onClick={() => setRegistrando(true)}>
            <Icono nombre="mas" /> {t(idioma, 'pap.registrar')}
          </button>
          <button type="button" className="secundario" onClick={onVolver}>
            {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <div className="conmutador-vista" role="group" aria-label={t(idioma, 'pap.titulo')}>
        {(['mapa', 'zonas', 'lista'] as Vista[]).map((v) => (
          <button key={v} type="button" aria-pressed={vista === v} onClick={() => setVista(v)}>
            {t(idioma, `pap.vista.${v}`)}
          </button>
        ))}
      </div>

      <div className="pap-filtros">
        <input
          type="search"
          value={busqueda}
          placeholder={t(idioma, 'pap.buscar')}
          onChange={(e) => setBusqueda(e.target.value)}
          aria-label={t(idioma, 'pap.buscar')}
        />
        <div className="conmutador-vista" role="group" aria-label={t(idioma, 'pap.estadoTitulo')}>
          <button type="button" aria-pressed={filtro === 'TODAS'} onClick={() => setFiltro('TODAS')}>
            {t(idioma, 'pap.todas')} <span className="pastilla neutra">{(puertas ?? []).length}</span>
          </button>
          {ESTADOS_PUERTA.map((e) => (
            <button key={e} type="button" aria-pressed={filtro === e} onClick={() => setFiltro(e)}>
              {t(idioma, `pap.estado.${e}`)}{' '}
              <span className="pastilla neutra">
                {(puertas ?? []).filter((p) => p.estado === e).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {puertas === null && <p>{t(idioma, 'app.cargando')}</p>}

      {puertas !== null && puertas.length === 0 && (
        <section className="tarjeta">
          <p className="suave">{t(idioma, 'pap.vacio')}</p>
          <button type="button" className="pap-boton-grande" onClick={() => setRegistrando(true)}>
            <Icono nombre="mas" /> {t(idioma, 'pap.registrar')}
          </button>
        </section>
      )}

      {puertas !== null && puertas.length > 0 && vista === 'mapa' && (
        <MapaPuertas
          puertas={visibles}
          seleccionada={seleccionada}
          idioma={idioma}
          onSeleccionar={setSeleccionada}
          onAbrir={setAbierta}
        />
      )}

      {puertas !== null && puertas.length > 0 && vista === 'zonas' && (
        <section className="tarjeta">
          <h2>{t(idioma, 'pap.zonasRecorridas')}</h2>
          <div className="lista">
            {zonas.map(([nombre, datos]) => (
              <div key={nombre} className="fila">
                <Icono nombre="pin" />
                <span className="crecer">
                  <b>{nombre}</b>
                  <small className="suave">
                    {t(idioma, 'pap.zonaResumen', {
                      puertas: datos.puertas,
                      atendidas: datos.atendidas,
                    })}
                    {datos.ultima ? ` · ${fechaLarga(datos.ultima, idioma)}` : ''}
                  </small>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {puertas !== null && puertas.length > 0 && vista === 'lista' && (
        <section className="tarjeta">
          {visibles.length === 0 ? (
            <p className="suave">{t(idioma, 'pap.sinEnEsteFiltro')}</p>
          ) : (
            <div className="lista">
              {visibles.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="fila fila-pulsable"
                  onClick={() => setAbierta(p.id)}
                >
                  <span className={`pastilla pap-${p.estado.toLowerCase()}`}>
                    {t(idioma, `pap.estado.${p.estado}`)}
                  </span>
                  <span className="crecer">
                    <b>{p.direccion}</b>
                    <small className="suave">
                      {[p.zona, p.municipio].filter(Boolean).join(' · ')}
                      {` · ${fechaLarga(p.visitadaEn, idioma)}`}
                      {p.contactoNombre ? ` · ${p.contactoNombre}` : ''}
                    </small>
                  </span>
                  {p.fotos.length > 0 && (
                    <span className="chip-area">
                      <Icono nombre="camara" /> {p.fotos.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}
